// ───────────────────────────────────────────────────────────────────────────
// Demo core: waste catalog
// 14 categories and ~40 materials with fixed ids (documented in docs/demo-fixtures.md:
// category -> material, alias material_type_id === material id in the demo).
// Emission factors are tCO2e avoided per tonne recycled, prices are SAR/kg the
// recycler pays the outlet; both are realistic orders of magnitude, not quotes.
// ───────────────────────────────────────────────────────────────────────────

import { ids } from './constants';

export type DemoCategoryCode =
  | 'cardboard'
  | 'paper'
  | 'plastic'
  | 'metal'
  | 'glass'
  | 'organic'
  | 'wood'
  | 'textiles'
  | 'rubber'
  | 'e_waste'
  | 'oil'
  | 'chemical'
  | 'construction'
  | 'mixed';

export type DemoPhysicalState = 'solid' | 'liquid' | 'sludge';

export interface DemoCategory {
  id: string;
  code: DemoCategoryCode;
  name_en: string;
  name_ar: string;
  sort_order: number;
}

export interface DemoMaterial {
  id: string;
  /** Alias id used by orders/containers; equal to `id` in the demo. */
  material_type_id: string;
  category_code: DemoCategoryCode;
  code: string;
  slug: string;
  name_en: string;
  name_ar: string;
  default_physical_state: DemoPhysicalState;
  hazardous: boolean;
  ewc_codes: string[];
  emission_factor_tco2e_per_t: number;
  status: 'published';
}

const CATEGORY_ROWS: Array<[DemoCategoryCode, string, string]> = [
  ['cardboard', 'Cardboard', 'كرتون'],
  ['paper', 'Paper', 'ورق'],
  ['plastic', 'Plastic', 'بلاستيك'],
  ['metal', 'Metal', 'معادن'],
  ['glass', 'Glass', 'زجاج'],
  ['organic', 'Organic', 'مخلفات عضوية'],
  ['wood', 'Wood', 'خشب'],
  ['textiles', 'Textiles', 'منسوجات'],
  ['rubber', 'Rubber', 'مطاط'],
  ['e_waste', 'Electronic Waste', 'نفايات إلكترونية'],
  ['oil', 'Oil', 'زيوت'],
  ['chemical', 'Chemical Waste', 'نفايات كيميائية'],
  ['construction', 'Construction & Demolition', 'مخلفات البناء والهدم'],
  ['mixed', 'Mixed Waste', 'نفايات مختلطة'],
];

export const DEMO_CATEGORIES: DemoCategory[] = CATEGORY_ROWS.map(([code, name_en, name_ar], i) => ({
  id: ids.category(i + 1),
  code,
  name_en,
  name_ar,
  sort_order: i + 1,
}));

// [code, category, name_en, name_ar, state, hazardous, ewc codes, tCO2e/t, SAR/kg]
type MaterialRow = [string, DemoCategoryCode, string, string, DemoPhysicalState, boolean, string[], number, number];

const MATERIAL_ROWS: MaterialRow[] = [
  ['occ', 'cardboard', 'OCC Cardboard', 'كرتون مموج مستعمل (OCC)', 'solid', false, ['15 01 01'], 0.9, 0.45],
  ['mixed_cardboard', 'cardboard', 'Mixed Cardboard', 'كرتون مختلط', 'solid', false, ['15 01 01'], 0.85, 0.35],
  ['beverage_cartons', 'cardboard', 'Beverage Cartons', 'عبوات المشروبات الكرتونية', 'solid', false, ['15 01 05'], 0.6, 0.15],
  ['mixed_paper', 'paper', 'Mixed Paper', 'ورق مختلط', 'solid', false, ['20 01 01'], 0.7, 0.3],
  ['office_paper', 'paper', 'Office Paper', 'ورق مكتبي', 'solid', false, ['20 01 01'], 1.0, 0.6],
  ['newspaper', 'paper', 'Newspaper & Magazines', 'صحف ومجلات', 'solid', false, ['20 01 01'], 0.8, 0.35],
  ['shredded_paper', 'paper', 'Shredded Paper', 'ورق مفروم', 'solid', false, ['20 01 01'], 0.7, 0.25],
  ['pet', 'plastic', 'PET Bottles', 'قوارير PET', 'solid', false, ['15 01 02'], 1.5, 1.4],
  ['hdpe', 'plastic', 'HDPE Containers', 'عبوات HDPE', 'solid', false, ['15 01 02'], 1.2, 1.2],
  ['ldpe_film', 'plastic', 'LDPE Film', 'أغشية LDPE', 'solid', false, ['15 01 02'], 1.1, 0.9],
  ['pp', 'plastic', 'PP Rigid Plastic', 'بلاستيك PP صلب', 'solid', false, ['15 01 02'], 1.1, 1.0],
  ['pvc', 'plastic', 'PVC', 'بلاستيك PVC', 'solid', false, ['17 02 03'], 0.9, 0.4],
  ['ps', 'plastic', 'Polystyrene (PS/EPS)', 'بوليسترين (PS/EPS)', 'solid', false, ['15 01 02'], 1.3, 0.5],
  ['mixed_plastic', 'plastic', 'Mixed Plastics', 'بلاستيك مختلط', 'solid', false, ['20 01 39'], 0.9, 0.3],
  ['aluminium_cans', 'metal', 'Aluminium Cans', 'علب الألمنيوم', 'solid', false, ['15 01 04'], 9.0, 4.5],
  ['steel_scrap', 'metal', 'Steel Scrap', 'خردة الحديد', 'solid', false, ['17 04 05'], 1.4, 0.9],
  ['copper', 'metal', 'Copper', 'نحاس', 'solid', false, ['17 04 01'], 3.5, 28.0],
  ['tin_cans', 'metal', 'Tin & Steel Cans', 'علب الصفيح', 'solid', false, ['15 01 04'], 1.3, 0.6],
  ['clear_glass', 'glass', 'Clear Glass', 'زجاج شفاف', 'solid', false, ['15 01 07'], 0.3, 0.12],
  ['coloured_glass', 'glass', 'Coloured Glass', 'زجاج ملون', 'solid', false, ['15 01 07'], 0.28, 0.08],
  ['food_waste', 'organic', 'Food Waste', 'مخلفات الطعام', 'solid', false, ['20 01 08'], 0.5, 0.05],
  ['green_waste', 'organic', 'Green & Garden Waste', 'مخلفات الحدائق', 'solid', false, ['20 02 01'], 0.3, 0.03],
  ['coffee_grounds', 'organic', 'Coffee Grounds', 'تفل القهوة', 'solid', false, ['20 01 08'], 0.45, 0.05],
  ['wood_pallets', 'wood', 'Wood Pallets', 'منصات خشبية', 'solid', false, ['15 01 03'], 0.6, 0.4],
  ['sawdust', 'wood', 'Sawdust', 'نشارة الخشب', 'solid', false, ['03 01 05'], 0.4, 0.1],
  ['mdf', 'wood', 'MDF & Chipboard', 'ألواح MDF', 'solid', false, ['17 02 01'], 0.45, 0.05],
  ['mixed_textiles', 'textiles', 'Mixed Textiles', 'منسوجات مختلطة', 'solid', false, ['20 01 11'], 3.0, 0.8],
  ['cotton_rags', 'textiles', 'Cotton Rags', 'خرق قطنية', 'solid', false, ['20 01 10'], 2.5, 1.0],
  ['tyres', 'rubber', 'End-of-life Tyres', 'إطارات مستعملة', 'solid', false, ['16 01 03'], 0.6, 0.2],
  ['rubber_scrap', 'rubber', 'Rubber Scrap', 'خردة مطاط', 'solid', false, ['19 12 04'], 0.5, 0.25],
  ['small_appliances', 'e_waste', 'Small Appliances (WEEE)', 'أجهزة كهربائية صغيرة', 'solid', false, ['20 01 36'], 1.8, 1.5],
  ['batteries', 'e_waste', 'Batteries', 'بطاريات', 'solid', true, ['20 01 33*'], 1.2, 2.5],
  ['printer_cartridges', 'e_waste', 'Printer Cartridges', 'خراطيش الطابعات', 'solid', false, ['08 03 18'], 1.5, 3.0],
  ['it_equipment', 'e_waste', 'IT Equipment', 'أجهزة تقنية المعلومات', 'solid', false, ['16 02 14'], 2.0, 4.0],
  ['uco', 'oil', 'Used Cooking Oil (UCO)', 'زيت الطهي المستعمل', 'liquid', false, ['20 01 25'], 2.5, 2.2],
  ['lubricant_oil', 'oil', 'Used Lubricant Oil', 'زيت التشحيم المستعمل', 'liquid', true, ['13 02 05*'], 1.1, 0.8],
  ['solvents', 'chemical', 'Spent Solvents', 'مذيبات مستهلكة', 'liquid', true, ['14 06 03*'], 0.6, 0.3],
  ['paint_waste', 'chemical', 'Paint & Coatings', 'مخلفات الطلاء', 'sludge', true, ['08 01 11*'], 0.4, 0.1],
  ['contaminated_packaging', 'chemical', 'Contaminated Packaging', 'عبوات ملوثة', 'solid', true, ['15 01 10*'], 0.3, 0.05],
  ['concrete', 'construction', 'Concrete & Rubble', 'خرسانة وركام', 'solid', false, ['17 01 01'], 0.01, 0.01],
  ['gypsum', 'construction', 'Gypsum Board', 'ألواح الجبس', 'solid', false, ['17 08 02'], 0.05, 0.02],
  ['mixed_municipal', 'mixed', 'Mixed Municipal Waste', 'نفايات بلدية مختلطة', 'solid', false, ['20 03 01'], 0.2, 0.02],
  ['mixed_recyclables', 'mixed', 'Mixed Recyclables', 'مواد قابلة للتدوير مختلطة', 'solid', false, ['15 01 06'], 0.8, 0.25],
];

export const DEMO_MATERIALS: DemoMaterial[] = MATERIAL_ROWS.map(
  ([code, category_code, name_en, name_ar, state, hazardous, ewc, factor], i) => {
    const id = ids.material(i + 1);
    return {
      id,
      material_type_id: id,
      category_code,
      code,
      slug: code.replace(/_/g, '-'),
      name_en,
      name_ar,
      default_physical_state: state,
      hazardous,
      ewc_codes: ewc,
      emission_factor_tco2e_per_t: factor,
      status: 'published' as const,
    };
  },
);

/** Material id by catalog code, e.g. DEMO_MATERIAL_IDS.occ. */
export const DEMO_MATERIAL_IDS: Record<string, string> = Object.fromEntries(DEMO_MATERIALS.map((m) => [m.code, m.id]));

/** SAR per kg the recycler pays, keyed by material id (every material has a price). */
export const MATERIAL_PRICE_SAR_PER_KG: Record<string, number> = Object.fromEntries(
  MATERIAL_ROWS.map((row, i) => [ids.material(i + 1), row[8]]),
);

const BY_ID = new Map(DEMO_MATERIALS.map((m) => [m.id, m]));

export function materialById(id: string): DemoMaterial | undefined {
  return BY_ID.get(id);
}

export function materialByCode(code: string): DemoMaterial | undefined {
  const id = DEMO_MATERIAL_IDS[code];
  return id ? BY_ID.get(id) : undefined;
}

export function materialsByCategory(code: string): DemoMaterial[] {
  return DEMO_MATERIALS.filter((m) => m.category_code === code);
}

export function categoryByCode(code: string): DemoCategory | undefined {
  return DEMO_CATEGORIES.find((c) => c.code === code);
}

function idsFor(codes: string[]): string[] {
  return codes.map((code) => {
    const id = DEMO_MATERIAL_IDS[code];
    if (!id) throw new Error(`[demo] unknown material code ${code}`);
    return id;
  });
}

/** The outlet's basket (9): what a supermarket chain separates at source. */
export const OUTLET_BASKET_MATERIAL_IDS: string[] = idsFor([
  'occ',
  'mixed_paper',
  'office_paper',
  'pet',
  'hdpe',
  'ldpe_film',
  'aluminium_cans',
  'uco',
  'wood_pallets',
]);

/** The recycler's basket (14): a superset of the outlet basket plus five streams it buys elsewhere. */
export const RECYCLER_BASKET_MATERIAL_IDS: string[] = idsFor([
  'occ',
  'mixed_paper',
  'office_paper',
  'pet',
  'hdpe',
  'ldpe_film',
  'aluminium_cans',
  'uco',
  'wood_pallets',
  'newspaper',
  'pp',
  'steel_scrap',
  'copper',
  'clear_glass',
]);
